import React from 'react';
import { Github, Linkedin, Mail, ChevronDown } from 'lucide-react';
import RevealOnScroll from '../../hooks/useRevealOnScroll';
import { useTranslation } from '../../hooks/useTranslation';

export const HeroSection = ({ scrollToSection }) => {
    const { t } = useTranslation();
    return (
        <section id="home" className="min-h-screen flex items-center justify-center relative px-6 pt-20">
            {/* Abstract background elements */}
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#d92323]/10 rounded-full blur-[100px] pointer-events-none"></div>
            <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#732424]/10 rounded-full blur-[80px] pointer-events-none"></div>

            <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
                <div className="space-y-6">
                    <RevealOnScroll>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d92323]/10 border border-[#d92323]/30 text-[#d92323] text-sm font-mono mb-4">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d92323] opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d92323]"></span>
                            </span>
                            Open to Opportunities
                        </div>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                            I Build <span className="text-[#d92323]">Intelligent</span> <br />
                            Systems.
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <p className="text-xl text-[#7b7b7b] max-w-lg leading-relaxed">
                            {t('heroGreeting')} <span className="text-white font-semibold">Taher Ayadi</span>.
                            {t('heroTitle')}
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={300}>
                        <div className="flex flex-wrap gap-4 pt-4">
                            <button onClick={() => scrollToSection('projects')} className="px-8 py-3 bg-[#d92323] hover:bg-[#b91b1b] text-white font-bold rounded hover:scale-105 transition-all duration-300 shadow-[0_0_20px_rgba(217,35,35,0.4)]">
                                {t('viewWork')}
                            </button>
                            <button onClick={() => scrollToSection('contact')} className="px-8 py-3 bg-transparent border border-[#7b7b7b] hover:border-white text-[#7b7b7b] hover:text-white font-bold rounded hover:bg-white/5 transition-all duration-300">
                                {t('contactMe')}
                            </button>
                        </div>
                    </RevealOnScroll>

                    <RevealOnScroll delay={400}>
                        <div className="flex items-center gap-6 pt-8 text-[#7b7b7b]">
                            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#d92323] transition-colors"><Github className="w-6 h-6" /></a>
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#d92323] transition-colors"><Linkedin className="w-6 h-6" /></a>
                            <a href="mailto:taher.ayadi@esprit.tn" className="hover:text-[#d92323] transition-colors"><Mail className="w-6 h-6" /></a>
                        </div>
                    </RevealOnScroll>
                </div>

                <RevealOnScroll delay={200}>
                    <div className="relative hidden md:block">
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#d92323] to-transparent opacity-20 rounded-2xl -rotate-6 transform translate-x-4 translate-y-4"></div>
                        <div className="relative bg-[#141414] border border-[#732424]/50 rounded-2xl p-8 shadow-2xl">
                            <div className="flex justify-between items-center mb-6 border-b border-[#732424]/30 pb-4">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 rounded-full bg-[#d92323]"></div>
                                    <div className="w-3 h-3 rounded-full bg-[#7b7b7b]"></div>
                                    <div className="w-3 h-3 rounded-full bg-[#7b7b7b]/50"></div>
                                </div>
                                <span className="text-xs font-mono text-[#7b7b7b]">developer_profile.py</span>
                            </div>
                            <pre className="font-mono text-sm overflow-x-auto text-[#7b7b7b]">
                                <code>
                                    <span className="text-[#d92323]">class</span> <span className="text-white">AI_Engineer</span>:
                                    {'\n'}    name = <span className="text-[#ffffff]">"Taher Ayadi"</span>
                                    {'\n'}    role = <span className="text-[#ffffff]">"AI Engineer"</span>
                                    {'\n'}    education = <span className="text-[#ffffff]">"ESPRIT"</span>
                                    {'\n'}    skills = [
                                    {'\n'}        <span className="text-[#d92323]">"Deep Learning"</span>,
                                    {'\n'}        <span className="text-[#d92323]">"Full Stack"</span>,
                                    {'\n'}        <span className="text-[#d92323]">"Computer Vision"</span>
                                    {'\n'}    ]
                                    {'\n'}    mission = <span className="text-[#ffffff]">"AI for Social Good"</span>
                                </code>
                            </pre>
                        </div>
                    </div>
                </RevealOnScroll>
            </div>

            <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-[#7b7b7b]">
                <ChevronDown className="w-6 h-6" />
            </div>
        </section>
    );
};